import { DASHBOARD_MODULE_ROUTES } from '#/api/dashboard/routes';

/** Dashboard（overview 與細項）最多 120 個曆月（含起訖）；超過回 400 QUERY_PARAM_INVALID */
export const DASHBOARD_MAX_MONTHS = 120;

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseYmd(value) {
  const match = YMD.exec(value ?? '');
  if (!match) {
    return null;
  }
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

function pad(value) {
  return String(value).padStart(2, '0');
}

/** 區間完整且 from <= to 才算有效（YYYY-MM-DD 可直接字串比較） */
export function isCompleteRange(range) {
  return Boolean(parseYmd(range?.from) && parseYmd(range?.to) && range.from <= range.to);
}

/** 含起訖的曆月數；區間不完整時回 0 */
export function countCalendarMonths(range) {
  const from = parseYmd(range?.from);
  const to = parseYmd(range?.to);
  if (!from || !to) {
    return 0;
  }
  return (to.year - from.year) * 12 + (to.month - from.month) + 1;
}

/**
 * response `period`（camelCase）→ `{ from, to, asOfDate }`。
 * 各 module 的 period key 不同（如 salesOrderDateFrom、receiptDateFrom）。
 */
export function readPeriod(moduleKey, period) {
  const route = DASHBOARD_MODULE_ROUTES[moduleKey];
  if (!route || !period) {
    return null;
  }
  const toCamel = (key) => key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
  return {
    from: period[toCamel(route.dateFromParam)],
    to: period[toCamel(route.dateToParam)],
    asOfDate: period.asOfDate,
  };
}

/** `YYYY-MM-DD` 前一天（UTC 計算，不受瀏覽器時區影響） */
export function previousDay(ymd) {
  const parsed = parseYmd(ymd);
  if (!parsed) {
    return undefined;
  }
  const date = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day - 1));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/**
 * 點選月份（YYYY-MM）與原查詢區間取交集；不擴大首尾部分月。
 * @returns {{ from: string, to: string } | null}
 */
export function intersectMonth(month, period) {
  const match = /^(\d{4})-(\d{2})$/.exec(month ?? '');
  if (!match || !period?.from || !period?.to) {
    return null;
  }
  const year = Number(match[1]);
  const monthIndex = Number(match[2]);
  const lastDay = new Date(Date.UTC(year, monthIndex, 0)).getUTCDate();
  const monthFrom = `${match[1]}-${match[2]}-01`;
  const monthTo = `${match[1]}-${match[2]}-${pad(lastDay)}`;
  const from = monthFrom > period.from ? monthFrom : period.from;
  const to = monthTo < period.to ? monthTo : period.to;
  return from <= to ? { from, to } : null;
}

/**
 * 鑽取列表 URL：`{listRoute}?status=X&{date_from}=..&{date_to}=..`。
 * 列表頁以 URL snake key 解析篩選並原樣送後端 search route。
 */
export function buildDrillDownHref(moduleKey, listRoute, { status, range, extra } = {}) {
  const route = DASHBOARD_MODULE_ROUTES[moduleKey];
  const params = new URLSearchParams();
  if (status) {
    params.set('status', status);
  }
  if (route && range?.from && range?.to) {
    params.set(route.dateFromParam, range.from);
    params.set(route.dateToParam, range.to);
  }
  Object.entries(extra ?? {}).forEach(([key, value]) => {
    if (value) {
      params.set(key, value);
    }
  });
  const query = params.toString();
  return query ? `${listRoute}?${query}` : listRoute;
}

/**
 * 以 `ymd` 所在月份為基準位移 `offset` 個月，回傳該月整月區間。
 * @returns {{ from: string, to: string } | null}
 */
export function shiftMonthRange(ymd, offset) {
  const parsed = parseYmd(ymd);
  if (!parsed) {
    return null;
  }
  const start = new Date(Date.UTC(parsed.year, parsed.month - 1 + offset, 1));
  const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0));
  const toYmd = (date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  return { from: toYmd(start), to: toYmd(end) };
}

/** recentMonthsRange 預設月數（本月＋前兩個月）；主控台日期區間目前預設本月（recentMonthsRange(1)） */
export const DASHBOARD_DEFAULT_MONTHS = 3;

/** Asia/Taipei 今天（YYYY-MM-DD），與後端 as_of_date 時區一致 */
export function todayInTaipei() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei' }).format(new Date());
}

/**
 * 含本月在內往前共 `months` 個整月。
 * @returns {{ from: string, to: string }}
 */
export function recentMonthsRange(months = DASHBOARD_DEFAULT_MONTHS, today = todayInTaipei()) {
  return {
    from: shiftMonthRange(today, -(months - 1)).from,
    to: shiftMonthRange(today, 0).to,
  };
}

/**
 * 整個區間前後平移 `offset` 個月，保留月數（以起日所在月為基準，結果對齊整月）。
 * @returns {{ from: string, to: string } | null}
 */
export function shiftRangeWindow(range, offset) {
  if (!isCompleteRange(range)) {
    return null;
  }
  const months = countCalendarMonths(range);
  const start = shiftMonthRange(range.from, offset);
  const end = shiftMonthRange(range.from, offset + months - 1);
  return { from: start.from, to: end.to };
}

/** 月別圖月數選項（後端 `trend_months`，1–120；預設 3） */
export const DASHBOARD_TREND_MONTH_OPTIONS = [3, 6, 12];

/**
 * `YYYY-MM` → 該月整月區間（月圖鑽取用；月圖期間由 trend_months 決定，與頁面日期區間無關）。
 * @returns {{ from: string, to: string } | null}
 */
export function monthRange(month) {
  return intersectMonth(month, { from: '0000-01-01', to: '9999-12-31' });
}

/**
 * 狀態卡鑽取 URL。
 * - 待辦卡（backlog）：只帶 status，不帶日期；逾期卡依 `card.overdue` 加條件（昨天＝as_of_date 前一天）。
 * - 完成卡（completion）：帶 status＋completion_date_from/to（完成事件日期）。
 */
export function buildCardHref(config, card, { kind, range, asOfDate }) {
  const extra = {};
  if (card.overdue === 'plannedShip') {
    extra.planned_ship_date_to = previousDay(asOfDate);
  } else if (card.overdue === 'scheduledStart') {
    extra.scheduled_start_date_to = previousDay(asOfDate);
  } else if (card.overdue === 'unreceived') {
    extra.overdue_unreceived = 'true';
  }
  if (kind === 'completion' && range?.from && range?.to) {
    extra.completion_date_from = range.from;
    extra.completion_date_to = range.to;
  }
  return buildDrillDownHref(config.key, config.listRoute, { status: card.status, extra });
}
